import type { TemplateResult } from 'lit';
import type {
  LoginSite,
  LoginSiteDomainScopeGrant,
  LoginGroup,
} from '../../../api/agwa-api-model';

export interface SiteWorkspaceContext {
  site: LoginSite | null;
  group: LoginGroup | null;
  domains: LoginSiteDomainScopeGrant[];
  loading: boolean;
  siteId: string;
  groupId: string;
}

export interface SiteWorkspaceModuleDescriptor {
  id: string;
  label: string;
  icon?: string;
  order?: number;
  /** Domain codes required to show this module. Module shows if site has ANY of these domains. */
  domains?: string[];
  /** Optional section header to group this tab under (e.g., "Configuration") */
  section?: string;
  /** Lazy-load function to import the component */
  load?: () => Promise<unknown>;
  /** Render function to produce the tab content */
  render: (context: SiteWorkspaceContext) => TemplateResult;
}

const moduleRegistry = new Map<string, SiteWorkspaceModuleDescriptor>();

export function registerSiteWorkspaceModule(
  descriptor: SiteWorkspaceModuleDescriptor
) {
  if (!descriptor.id || moduleRegistry.has(descriptor.id)) {
    throw new Error(
      `registerSiteWorkspaceModule: a module with id "${descriptor.id}" already exists.`
    );
  }
  moduleRegistry.set(descriptor.id, descriptor);
}

export function unregisterSiteWorkspaceModule(id: string) {
  moduleRegistry.delete(id);
}

/**
 * Get site workspace modules that should be visible for a site with given domains.
 * Filters by domain membership and sorts by order.
 */
export function listSiteWorkspaceModules(
  siteDomains: LoginSiteDomainScopeGrant[]
): SiteWorkspaceModuleDescriptor[] {
  const activeDomainCodes = new Set(
    siteDomains.filter((d) => d.active).map((d) => d.domainCode)
  );

  return Array.from(moduleRegistry.values())
    .filter((descriptor) => {
      // If no domains specified, always show
      if (!descriptor.domains || descriptor.domains.length === 0) {
        return true;
      }
      // Show if site has any of the required domains
      return descriptor.domains.some((d) => activeDomainCodes.has(d));
    })
    .sort((a, b) => {
      const orderA = a.order ?? 100;
      const orderB = b.order ?? 100;
      if (orderA === orderB) {
        return a.label.localeCompare(b.label);
      }
      return orderA - orderB;
    });
}
